// src/hooks/useChat.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import chatService from '../services/chat.service';
import socketService from '../services/socket.service';

export function useChat(currentUserId) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId]   = useState(null);
  const [messages, setMessages]           = useState([]);
  const [chatableUsers, setChatableUsers] = useState([]);
  const [typingUsers, setTypingUsers]     = useState(new Set());
  const [onlineUsers, setOnlineUsers]     = useState(new Set());
  const [loadingMsgs, setLoadingMsgs]     = useState(false);
  const [loadingConvs, setLoadingConvs]   = useState(false);
  const [page, setPage]                   = useState(1);
  const [hasMore, setHasMore]             = useState(false);

  const activeConvIdRef = useRef(null);
  const messagesRef     = useRef([]);   // mirror for socket callbacks
  const typingTimer     = useRef(null);

  // Keep refs in sync
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // ── Helpers ────────────────────────────────────────────────────────────
  const getPeerId = useCallback(
    (convId) => {
      const conv = conversations.find((c) => c.id === convId);
      if (!conv) return null;
      return conv.user1Id === currentUserId ? conv.user2Id : conv.user1Id;
    },
    [conversations, currentUserId]
  );

  // ── Load conversations ─────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const res = await chatService.getConversations();
      setConversations(res.data || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  // ── Load messages ──────────────────────────────────────────────────────
  const loadMessages = useCallback(async (convId, p = 1) => {
    setLoadingMsgs(true);
    try {
      const res = await chatService.getMessages(convId, p, 50);
      const incoming = res.messages || [];

      if (p === 1) {
        // Fresh load — replace all messages
        setMessages(incoming);
        messagesRef.current = incoming;
      } else {
        // Older messages — prepend without duplicates
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newOnes = incoming.filter((m) => !existingIds.has(m.id));
          const merged = [...newOnes, ...prev];
          messagesRef.current = merged;
          return merged;
        });
      }

      setPage(p);
      setHasMore(p < (res.totalPages || 1));
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  // ── Select conversation ────────────────────────────────────────────────
  const selectConversation = useCallback(
    async (convId) => {
      // Update ref immediately so socket events during load go to right conv
      activeConvIdRef.current = convId;
      setActiveConvId(convId);
      setTypingUsers(new Set());
      setPage(1);
      setHasMore(false);

      // Load messages FIRST, then set state — no flash of empty screen
      await loadMessages(convId, 1);

      // Mark as read
      chatService.markAsRead(convId).catch(() => {});
      socketService.getSocket()?.emit('chat:read', { conversationId: convId });

      // Clear unread badge in sidebar
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, unreadCount: 0 } : c))
      );
    },
    [loadMessages]
  );

  // ── Start new conversation ─────────────────────────────────────────────
  const startConversation = useCallback(
    async (peerId) => {
      const res  = await chatService.startConversation(peerId);
      const conv = res.data;
      setConversations((prev) => {
        if (prev.find((c) => c.id === conv.id)) return prev;
        return [{ ...conv, unreadCount: 0 }, ...prev];
      });
      await selectConversation(conv.id);
      return conv.id;
    },
    [selectConversation]
  );

  // ── Send message ───────────────────────────────────────────────────────
  const sendMessage = useCallback(
    (content) => {
      const convId = activeConvIdRef.current;
      if (!convId || !content?.trim()) return;

      const socket = socketService.getSocket();
      if (!socket?.connected) {
        console.warn('[Chat] Socket not connected');
        return;
      }

      // Optimistic update — show message immediately
      const tempId = `temp-${Date.now()}`;
      const tempMsg = {
        id:             tempId,
        conversationId: convId,
        senderId:       currentUserId,
        receiverId:     null,
        content:        content.trim(),
        messageType:    'TEXT',
        isRead:         false,
        createdAt:      new Date().toISOString(),
        sender:         { id: currentUserId, firstName: '', lastName: '', avatar: null },
        _isTemp:        true,
      };

      setMessages((prev) => {
        const next = [...prev, tempMsg];
        messagesRef.current = next;
        return next;
      });

      socket.emit(
        'chat:send',
        { conversationId: convId, content: content.trim() },
        (ack) => {
          if (!ack?.success) {
            // Remove optimistic message on failure
            console.error('[Chat] Message delivery failed');
            setMessages((prev) => {
              const next = prev.filter((m) => m.id !== tempId);
              messagesRef.current = next;
              return next;
            });
          }
          // Success: real message arrives via 'chat:message' event below
        }
      );

      // Stop typing
      const peerId = getPeerId(convId);
      if (peerId) socket.emit('chat:typing_stop', { conversationId: convId, receiverId: peerId });
      clearTimeout(typingTimer.current);
    },
    [currentUserId, getPeerId]
  );

  // ── Typing ─────────────────────────────────────────────────────────────
  const handleTyping = useCallback(() => {
    const convId = activeConvIdRef.current;
    if (!convId) return;
    const socket = socketService.getSocket();
    const peerId = getPeerId(convId);
    if (!socket || !peerId) return;

    socket.emit('chat:typing', { conversationId: convId, receiverId: peerId });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      socket.emit('chat:typing_stop', { conversationId: convId, receiverId: peerId });
    }, 2000);
  }, [getPeerId]);

  // ── Load more ──────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    const convId = activeConvIdRef.current;
    if (!convId || !hasMore || loadingMsgs) return;
    loadMessages(convId, page + 1);
  }, [hasMore, loadingMsgs, page, loadMessages]);

  // ── Socket listeners — with retry until socket is ready ────────────────
  useEffect(() => {
    let cleanup = null;

    const attachListeners = (socket) => {
      const onMessage = (msg) => {
        const currentConvId = activeConvIdRef.current;

        if (msg.conversationId === currentConvId) {
          setMessages((prev) => {
            // Remove all temp messages + add real message (deduped)
            const withoutTemps = prev.filter((m) => !m._isTemp);
            if (withoutTemps.find((m) => m.id === msg.id)) return withoutTemps;
            const next = [...withoutTemps, msg];
            messagesRef.current = next;
            return next;
          });
          chatService.markAsRead(msg.conversationId).catch(() => {});
          socket.emit('chat:read', { conversationId: msg.conversationId });
        }

        // Update sidebar preview + unread badge
        setConversations((prev) => {
          const updated = prev.map((c) => {
            if (c.id !== msg.conversationId) return c;
            const isActive = msg.conversationId === currentConvId;
            const isMine   = msg.senderId === currentUserId;
            return {
              ...c,
              lastMessage:    msg,
              lastActivityAt: msg.createdAt,
              unreadCount:    isActive || isMine ? 0 : c.unreadCount + 1,
            };
          });
          return [...updated].sort(
            (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
          );
        });
      };

      const onTyping = ({ conversationId, senderId }) => {
        if (conversationId === activeConvIdRef.current)
          setTypingUsers((prev) => new Set([...prev, senderId]));
      };

      const onTypingStop = ({ conversationId, senderId }) => {
        if (conversationId === activeConvIdRef.current)
          setTypingUsers((prev) => { const s = new Set(prev); s.delete(senderId); return s; });
      };

      const onUserOnline  = ({ userId }) =>
        setOnlineUsers((prev) => new Set([...prev, userId]));
      const onUserOffline = ({ userId }) =>
        setOnlineUsers((prev) => { const s = new Set(prev); s.delete(userId); return s; });

      socket.on('chat:message',     onMessage);
      socket.on('chat:typing',      onTyping);
      socket.on('chat:typing_stop', onTypingStop);
      socket.on('chat:user_online', onUserOnline);
      socket.on('chat:user_offline',onUserOffline);

      return () => {
        socket.off('chat:message',     onMessage);
        socket.off('chat:typing',      onTyping);
        socket.off('chat:typing_stop', onTypingStop);
        socket.off('chat:user_online', onUserOnline);
        socket.off('chat:user_offline',onUserOffline);
      };
    };

    // Try immediately — if not ready yet, poll every 500ms
    const tryAttach = () => {
      const socket = socketService.getSocket();
      if (socket) {
        cleanup = attachListeners(socket);
        return true;
      }
      return false;
    };

    if (!tryAttach()) {
      const interval = setInterval(() => {
        if (tryAttach()) clearInterval(interval);
      }, 500);
      return () => {
        clearInterval(interval);
        if (cleanup) cleanup();
      };
    }

    return () => { if (cleanup) cleanup(); };
  }, [currentUserId]);

  // ── Initial load ───────────────────────────────────────────────────────
  useEffect(() => {
    loadConversations();
    chatService
      .getChatableUsers()
      .then((res) => setChatableUsers(res.data || []))
      .catch((err) => console.error('Failed to load users:', err));
  }, [loadConversations]);

  return {
    conversations, messages, chatableUsers,
    activeConvId, typingUsers, onlineUsers,
    loadingMsgs, loadingConvs, hasMore,
    selectConversation, startConversation, sendMessage,
    handleTyping, loadMore,
  };
}

export default useChat;